import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { curatorApi } from '@/api/users';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { StudentLessonPlayerPage } from '@/pages/student/Lesson/StudentLessonPlayerPage';

/**
 * Урок под чужой учёткой: куратор и админ видят ответы ученика.
 *
 * ⚠️ ЗДЕСЬ НЕТ КОПИИ ПЛЕЕРА, И ЭТО ГЛАВНОЕ РЕШЕНИЕ. Плеер — восемьсот строк с
 * двумя раскладками; форк разъехался бы с ученическим за неделю, и разбор
 * показывал бы уже не то, что видит ученик. Вместо форка — тот же плеер с
 * признаком «только чтение», по которому он выключает все места, где пишет на
 * сервер.
 *
 * Шапка над плеером — по эталону: чей это урок и как выйти обратно к списку.
 */
export function StudentLessonReviewPage() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();

  // Раздел, из которого пришли: админ обязан вернуться в админку, а не в
  // кураторскую — там его встретит проверка роли.
  const base = pathname.startsWith('/admin') ? '/admin' : '/curator';
  const backTo = `${base}/students/${userId}/progress`;

  const student = useQuery({
    queryKey: ['staff', 'student-card', userId],
    queryFn: () => curatorApi.getStudent(userId!),
    enabled: !!userId,
  });

  const name = student.data?.full_name || t('curatorStudents.studentFallback');

  // Телефон: шапку рисует сам плеер — одну, с «назад», уроком и именем (docs/CURATOR-MOBILE-DESIGN.md §4.2).
  if (isMobile) {
    return userId ? <StudentLessonPlayerPage review={{ studentId: userId, backTo, studentName: name }} /> : null;
  }

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center gap-3">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          {t('lessonReview.otherLesson')}
        </Link>

        <span className="hidden h-5 w-px bg-outline-variant sm:block" />

        <Avatar name={name} src={student.data?.avatar_url} size="sm" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-on-surface">{name}</p>
          <p className="text-xs text-on-surface-variant">{t('lessonReview.readOnly')}</p>
        </div>
      </Card>

      {userId && <StudentLessonPlayerPage review={{ studentId: userId, backTo }} />}
    </div>
  );
}
